/**
 * Admin auth/session lifecycle.
 *
 * Phase 1.2 of the `frontend/admin/admin.js` decomposition. Owns:
 *
 *   - `setSessionExpiry(ttlMs)` — records a non-sensitive expiry timestamp in
 *     localStorage so the UI knows when to redirect to login.
 *   - `getToken()` — returns null. Token lives in an HttpOnly cookie that JS
 *     cannot read; kept on the surface for any caller that still references it.
 *   - `isSessionValid()` — checks the localStorage timestamp.
 *   - `clearToken()` — cleans up local state, calls `/auth/logout` to revoke
 *     the JWT and clear the cookie server-side, fires the configured hooks.
 *
 * Boot contract: `admin.html` loads this script before `admin.js`. `admin.js`
 * calls `adminAuth.configure({...})` once on startup to inject the
 * UI-side-effects (status badge refresh, notification-socket lifecycle, shift
 * cleanup) that still live there until later phases extract them.
 *
 * Phase 3.2.a: dropped the IIFE wrapper; module is now ESM (strict by
 * default). Public surface exported as named exports + still attached to
 * `window.adminAuth` for view modules that haven't migrated yet.
 */

const STORAGE_KEY_EXPIRY = 'admin_token_exp';
const STORAGE_KEY_LEGACY = 'admin_token'; // pre-cookie token, cleared on logout
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const DEFAULT_LOGOUT_URL = '/auth/logout';

// No-op defaults so the module is usable in isolation (e.g. unit tests)
// without the surrounding admin SPA wired up.
const noop = () => {};
let defaultTtlMs = DEFAULT_TTL_MS;
let logoutUrl = DEFAULT_LOGOUT_URL;
let onChange = noop;
let onStart = noop;
let onEnd = noop;

export function configure(options) {
  if (!options) return;
  if (typeof options.defaultTtlMs === 'number' && options.defaultTtlMs > 0) {
    defaultTtlMs = options.defaultTtlMs;
  }
  if (typeof options.logoutUrl === 'string' && options.logoutUrl) {
    logoutUrl = options.logoutUrl;
  }
  if (typeof options.onChange === 'function') onChange = options.onChange;
  if (typeof options.onStart === 'function') onStart = options.onStart;
  if (typeof options.onEnd === 'function') onEnd = options.onEnd;
}

export function setSessionExpiry(ttlMs) {
  const ttl = typeof ttlMs === 'number' && ttlMs > 0 ? ttlMs : defaultTtlMs;
  const expiresAt = Date.now() + ttl;
  try {
    localStorage.setItem(STORAGE_KEY_EXPIRY, String(expiresAt));
  } catch {}
  try { onChange(); } catch {}
  try { onStart(); } catch {}
}

// The token now rides in an HttpOnly cookie (see /auth/login server-side).
// JavaScript can't read it. Kept here so callers that still reach for a
// token symbol get a defined value rather than ReferenceError.
export function getToken() {
  return null;
}

export function isSessionValid() {
  let raw = '0';
  try {
    raw = localStorage.getItem(STORAGE_KEY_EXPIRY) || '0';
  } catch {
    return false;
  }
  const exp = Number(raw);
  return Boolean(exp && Date.now() < exp);
}

export function clearToken() {
  try { localStorage.removeItem(STORAGE_KEY_LEGACY); } catch {}
  try { localStorage.removeItem(STORAGE_KEY_EXPIRY); } catch {}
  // Tell the server to revoke the JWT and clear the HttpOnly cookie. We
  // intentionally don't await this: client-side cleanup must run even if
  // the network call fails (e.g. user is offline).
  try {
    fetch(logoutUrl, {
      method: 'POST',
      credentials: 'same-origin',
    }).catch(() => {});
  } catch {}
  try { onChange(); } catch {}
  try { onEnd(); } catch {}
}

// Phase 3.2.e: every caller now uses `import` directly. The
// `window.adminAuth` global no longer exists.
